import { BufferAttribute, BufferGeometry, Mesh, type Material } from 'three'
import type { FlipperDevice, MachineDefinition, MachineDevice } from '@core/machine'
import type { BuiltDevice, DeviceRuntime } from './deviceMeshes'
import type { Finish, PartPalette } from './partsKit'
import type asset from '../../SourceArt/PierFlippers/mesh.json'

let data: typeof asset | undefined
export async function prepareAuthoredFlippers(): Promise<void> {
  data ??= (await import('../../SourceArt/PierFlippers/mesh.json')).default
}
const near = (a: number, b: number) => Math.abs(a - b) <= 1e-9
function matches(device: MachineDevice | undefined, expected: FlipperDevice): boolean {
  return device?.kind === 'flipper' && device.material === expected.material
    && device.handedness === expected.handedness
    && near(device.pivot[0], expected.pivot[0]) && near(device.pivot[1], expected.pivot[1])
    && near(device.length, expected.length) && near(device.radiusPivot, expected.radiusPivot)
    && near(device.radiusTip, expected.radiusTip) && near(device.restAngle, expected.restAngle)
    && near(device.sweep, expected.sweep)
}
function instanceFor(definition: MachineDefinition, device: MachineDevice) {
  const instance = data?.machines[definition.machineId as keyof typeof asset.machines]?.find(item => item.id === device.id)
  if (instance === undefined) return undefined
  const expected = instance.device as unknown as FlipperDevice
  return matches(device, expected) && matches(definition.devices.find(d => d.id === device.id), expected) ? instance : undefined
}

/** Change only the three local visual children of an existing solver-driven flipper. */
export function withAuthoredFlippers(built: BuiltDevice, device: MachineDevice,
  definition: MachineDefinition, palette: PartPalette, illustrated: boolean,
  runtime: DeviceRuntime = {}, steel?: Material): BuiltDevice {
  if (!illustrated || device.kind !== 'flipper' || built.group === undefined) return built
  const instance = instanceFor(definition, device)
  if (instance === undefined) return built
  const live = runtime.flipper
  if (live !== undefined && (!near(live.pivotX, device.pivot[0]) || !near(live.pivotY, device.pivot[1])
    || !near(live.length, device.length) || !near(live.radiusPivot, device.radiusPivot)
    || !near(live.radiusTip, device.radiusTip) || !near(live.restAngle, device.restAngle)
    || live.direction !== (device.handedness === 'left' ? 1 : -1)
    || !near(live.endAngle, device.restAngle + live.direction * device.sweep))) return built
  const group = built.group
  if (!near(group.position.x, device.pivot[0]) || !near(group.position.y, device.pivot[1])
    || !near(group.position.z, device.radiusPivot) || !near(group.rotation.z, device.restAngle)) return built
  const children = [...group.children]
  if (children.length !== 3 || !children.every(child => child instanceof Mesh && child.name === '')) return built
  const bat = (children[0] as Mesh).geometry
  const parameters = (bat as BufferGeometry & { parameters?: { radiusTop: number; radiusBottom: number; height: number } }).parameters
  if (bat.type !== 'CylinderGeometry' || parameters === undefined || !near(parameters.radiusTop, device.radiusTip)
    || !near(parameters.radiusBottom, device.radiusPivot) || !near(parameters.height, device.length)) return built
  const geometries: BufferGeometry[] = []
  const meshes: Mesh[] = []
  try {
    const finishes = new Set<string>()
    if (instance.parts.length !== 3) throw new Error('Flipper requires three material groups')
    for (const part of instance.parts) {
      if (!['HardwareSteel', 'HardwareIvory', 'HardwareRubber'].includes(part.finish) || finishes.has(part.finish))
        throw new Error('Unsupported or duplicate flipper finish')
      finishes.add(part.finish)
      const count = part.positions.length / 3
      if (count === 0 || !Number.isInteger(count) || part.normals.length !== part.positions.length
        || part.uvs.length !== count * 2 || part.triangles.length === 0 || part.triangles.length % 3 !== 0
        || ![...part.positions, ...part.normals, ...part.uvs].every(Number.isFinite)
        || !part.triangles.every(index => Number.isInteger(index) && index >= 0 && index < count))
        throw new Error('Invalid flipper mesh arrays')
      const geometry = new BufferGeometry()
      geometries.push(geometry)
      geometry.setAttribute('position', new BufferAttribute(new Float32Array(part.positions), 3))
      geometry.setAttribute('normal', new BufferAttribute(new Float32Array(part.normals), 3))
      geometry.setAttribute('uv', new BufferAttribute(new Float32Array(part.uvs), 2))
      geometry.setIndex(part.triangles)
      geometry.computeBoundingBox(); geometry.computeBoundingSphere()
      const finish = (part.finish[0]!.toLowerCase() + part.finish.slice(1)) as Finish
      const material = part.finish === 'HardwareIvory' ? palette.satinFlipperPolymer()
        : part.finish === 'HardwareSteel' && steel !== undefined ? steel : palette.get(finish)
      const mesh = new Mesh(geometry, material)
      mesh.name = `${device.id}.flipper.authored.${part.finish}`
      meshes.push(mesh)
    }
  } catch (error) {
    geometries.forEach(geometry => geometry.dispose())
    throw error
  }
  // Preserve the group, animator, rebind closure and their runtime authority. No transform writes.
  group.remove(...children); group.add(...meshes)
  // Retired primitive geometry still belongs to the caller and is disposed once with this build.
  return { ...built, geometries: [...built.geometries, ...geometries] }
}
